import React, { MouseEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Settings } from '@mui/icons-material'
import { Box, Button, Grid, IconButton, Popover, Tooltip, Typography } from '@mui/material'

import { SliderNumberInput } from '@components/SliderNumberInput'

import {
  TIME_LINE_OBJECT_X_CELL_MAX_HEIGHT,
  TIME_LINE_OBJECT_X_CELL_MAX_PREVIEW_COLUMNS_COUNT,
  TIME_LINE_OBJECT_X_CELL_MAX_PREVIEW_ROWS_COUNT,
  TIME_LINE_OBJECT_X_CELL_MAX_WIDTH,
  TIME_LINE_OBJECT_X_CELL_MIN_HEIGHT,
  TIME_LINE_OBJECT_X_CELL_MIN_PREVIEW_COLUMNS_COUNT,
  TIME_LINE_OBJECT_X_CELL_MIN_PREVIEW_ROWS_COUNT,
  TIME_LINE_OBJECT_X_CELL_MIN_WIDTH,
  TIME_LINE_SLIDER_STEP,
} from '@constants'

type PreviewSettingsPopoverProps = {
  previewWidth: number
  previewHeight: number
  previewColumns: number
  previewRows: number
  onWidthChange: (value: number) => void
  onHeightChange: (value: number) => void
  onColumnsChange: (value: number) => void
  onRowsChange: (value: number) => void
  onApplyToConfig?: (width: number, height: number) => void
}

export const PreviewSettingsPopover: React.FC<PreviewSettingsPopoverProps> = ({
  previewWidth,
  previewHeight,
  previewColumns,
  previewRows,
  onColumnsChange,
  onRowsChange,
  onHeightChange,
  onWidthChange,
  onApplyToConfig,
}) => {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'preview-settings-popover' : undefined

  const handleWidthSliderChange = (_event: Event, newValue: number | number[]) => {
    const value = Array.isArray(newValue) ? newValue[0] : newValue

    onWidthChange(value)
  }

  const handleHeightSliderChange = (_event: Event, newValue: number | number[]) => {
    const value = Array.isArray(newValue) ? newValue[0] : newValue

    onHeightChange(value)
  }

  const handleWidthInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onWidthChange(Number(event.target.value))
  }

  const handleHeightInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onHeightChange(Number(event.target.value))
  }

  const handleColumnsSliderChange = (_event: Event, newValue: number | number[]) => {
    const value = Array.isArray(newValue) ? newValue[0] : newValue

    onColumnsChange(value)
  }

  const handleRowsSliderChange = (_event: Event, newValue: number | number[]) => {
    const value = Array.isArray(newValue) ? newValue[0] : newValue

    onRowsChange(value)
  }

  const handleColumnsInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onColumnsChange(Number(event.target.value))
  }

  const handleRowsInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onRowsChange(Number(event.target.value))
  }

  const handleWidthInputBlur = () => {
    if (isNaN(previewWidth) || previewWidth < TIME_LINE_OBJECT_X_CELL_MIN_WIDTH) {
      onWidthChange(TIME_LINE_OBJECT_X_CELL_MIN_WIDTH)

      return
    }

    if (previewWidth > TIME_LINE_OBJECT_X_CELL_MAX_WIDTH) {
      onWidthChange(TIME_LINE_OBJECT_X_CELL_MAX_WIDTH)
    }
  }

  const handleHeightInputBlur = () => {
    if (isNaN(previewHeight) || previewHeight < TIME_LINE_OBJECT_X_CELL_MIN_HEIGHT) {
      onHeightChange(TIME_LINE_OBJECT_X_CELL_MIN_HEIGHT)

      return
    }

    if (previewHeight > TIME_LINE_OBJECT_X_CELL_MAX_HEIGHT) {
      onHeightChange(TIME_LINE_OBJECT_X_CELL_MAX_HEIGHT)
    }
  }

  const handleColumnsInputBlur = () => {
    if (
      isNaN(previewColumns) ||
      previewColumns < TIME_LINE_OBJECT_X_CELL_MIN_PREVIEW_COLUMNS_COUNT
    ) {
      onColumnsChange(TIME_LINE_OBJECT_X_CELL_MIN_PREVIEW_COLUMNS_COUNT)

      return
    }

    if (previewColumns > TIME_LINE_OBJECT_X_CELL_MAX_PREVIEW_COLUMNS_COUNT) {
      onColumnsChange(TIME_LINE_OBJECT_X_CELL_MAX_PREVIEW_COLUMNS_COUNT)
    }
  }

  const handleRowsInputBlur = () => {
    if (isNaN(previewRows) || previewRows < TIME_LINE_OBJECT_X_CELL_MIN_PREVIEW_ROWS_COUNT) {
      onRowsChange(TIME_LINE_OBJECT_X_CELL_MIN_PREVIEW_ROWS_COUNT)

      return
    }

    if (previewRows > TIME_LINE_OBJECT_X_CELL_MAX_PREVIEW_ROWS_COUNT) {
      onRowsChange(TIME_LINE_OBJECT_X_CELL_MAX_PREVIEW_ROWS_COUNT)
    }
  }

  const handleOnClickPopover = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation()
  }

  const handleApplyToConfig = () => {
    onApplyToConfig?.(previewWidth, previewHeight)
    handleClose()
  }

  return (
    <>
      <Tooltip title={t('timeLinesCreateOrEdit.objectX.cell.previewSettings')}>
        <IconButton size='small' onClick={handleClick}>
          <Settings />
        </IconButton>
      </Tooltip>
      <Popover
        anchorEl={anchorEl}
        id={id}
        open={open}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiPaper-root': {
            minWidth: 600,
          },
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClick={handleOnClickPopover}
        onClose={handleClose}
      >
        <Box sx={{ p: 2, minWidth: 600 }}>
          <Typography sx={{ mb: 2 }} variant='subtitle2'>
            {t('timeLinesCreateOrEdit.objectX.cell.previewSettings')}
          </Typography>

          <Grid container spacing={3}>
            <Grid item md={6} xs={12}>
              <SliderNumberInput
                label={t('timeLinesCreateOrEdit.objectX.cell.width')}
                maxValue={TIME_LINE_OBJECT_X_CELL_MAX_WIDTH}
                minValue={TIME_LINE_OBJECT_X_CELL_MIN_WIDTH}
                step={TIME_LINE_SLIDER_STEP}
                value={previewWidth}
                onInputBlur={handleWidthInputBlur}
                onInputChange={handleWidthInputChange}
                onSliderChange={handleWidthSliderChange}
              />
              <SliderNumberInput
                label={t('timeLinesCreateOrEdit.objectX.cell.height')}
                maxValue={TIME_LINE_OBJECT_X_CELL_MAX_HEIGHT}
                minValue={TIME_LINE_OBJECT_X_CELL_MIN_HEIGHT}
                step={TIME_LINE_SLIDER_STEP}
                value={previewHeight}
                onInputBlur={handleHeightInputBlur}
                onInputChange={handleHeightInputChange}
                onSliderChange={handleHeightSliderChange}
              />
            </Grid>

            <Grid item md={6} xs={12}>
              <SliderNumberInput
                label={t('timeLinesCreateOrEdit.objectX.cell.previewColumns')}
                maxValue={TIME_LINE_OBJECT_X_CELL_MAX_PREVIEW_COLUMNS_COUNT}
                minValue={TIME_LINE_OBJECT_X_CELL_MIN_PREVIEW_COLUMNS_COUNT}
                step={TIME_LINE_SLIDER_STEP}
                value={previewColumns}
                onInputBlur={handleColumnsInputBlur}
                onInputChange={handleColumnsInputChange}
                onSliderChange={handleColumnsSliderChange}
              />
              <SliderNumberInput
                label={t('timeLinesCreateOrEdit.objectX.cell.previewRows')}
                maxValue={TIME_LINE_OBJECT_X_CELL_MAX_PREVIEW_ROWS_COUNT}
                minValue={TIME_LINE_OBJECT_X_CELL_MIN_PREVIEW_ROWS_COUNT}
                step={TIME_LINE_SLIDER_STEP}
                value={previewRows}
                onInputBlur={handleRowsInputBlur}
                onInputChange={handleRowsInputChange}
                onSliderChange={handleRowsSliderChange}
              />
            </Grid>
          </Grid>

          {onApplyToConfig && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                color='primary'
                size='small'
                variant='contained'
                onClick={handleApplyToConfig}
              >
                {t('timeLinesCreateOrEdit.objectX.cell.applyToConfig')}
              </Button>
            </Box>
          )}
        </Box>
      </Popover>
    </>
  )
}
